import { createElement, ReactNode } from "react";
import styled from "styled-components";
import { NEW_LOOK_NOW_PANEL } from "features/bots/NewLook/newLookBotUtils";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

/**
 * The "now" well's stats: a label (label role) over a figure (figure role).
 * A row of them (left block left-aligned, right block right-aligned) shares
 * its baselines, whatever the variant (trade levels, open trades, split).
 */
export const NowStats = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
`;
export const NowStat = styled.div<{ $isEnd?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${(p) => (p.$isEnd ? "flex-end" : "flex-start")};
  gap: ${NEW_LOOK_NOW_PANEL.labelGap}px;
  min-width: 0;
  text-align: ${(p) => (p.$isEnd ? "right" : "left")};
`;
export const NowStatLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
export const NowStatValue = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  height: 20px;
  ${newLookType.figure};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
/** A quieter part of a value (a level's price next to its distance). */
export const NowStatAside = styled.span`
  display: inline-flex;
  align-items: center;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;

/**
 * The calm card's "now" section: whitespace above, ONE hairline (between the
 * bot's result and what it is doing now) and the spare height of an
 * equal-height row, so the meta line can sit at the card's bottom.
 */
export const NowSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
`;
/** A header row: the title on the left, a value or state on the right. */
export const NowTop = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
`;
/** 13px / 600 ink; a muted 400 span carries the secondary part. */
export const NowTitle = styled.div`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  > span {
    font-weight: 400;
    color: ${(p) => getNewLookTokens(p.theme).secondary};
  }
`;
export const NowValue = styled.span`
  flex: none;
  font-size: 15px;
  line-height: 20px;
  font-weight: 500;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;
/** The right side of a header row when it is a state, not a figure. */
export const NowState = styled.span<{ $color?: string; $bold?: boolean }>`
  flex: none;
  font-size: 13px;
  line-height: 18px;
  font-weight: ${(p) => (p.$bold ? 600 : 500)};
  white-space: nowrap;
  color: ${(p) => p.$color || getNewLookTokens(p.theme).secondary};
`;
/** A row of two ends (stop / take-profit, low / high, long / short). */
export const NowEnds = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
  font-size: 13px;
  line-height: 18px;
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  b {
    font-weight: 500;
    color: ${(p) => getNewLookTokens(p.theme).ink};
  }
`;

const SEP_GAP = 14;
const MetaLine = styled.div<{ $pin: boolean; $gap: number }>`
  min-width: 0;
  /* Clips the separator of whichever item starts a line, so a wrapped line
     never ends (or starts) with a dangling "·". */
  overflow: hidden;
  font-size: 13px;
  line-height: 18px;
  font-weight: 400;
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  ${(p) =>
    p.$pin
      ? `margin-top: auto; padding-top: ${p.$gap}px;`
      : `margin-top: ${p.$gap}px;`}
`;
const MetaItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  row-gap: 2px;
  margin-left: -${SEP_GAP}px;
`;
const MetaItem = styled.span`
  position: relative;
  display: inline-block;
  margin-left: ${SEP_GAP}px;
  white-space: nowrap;
  &::before {
    content: "·";
    position: absolute;
    left: -${SEP_GAP / 2 + 2}px;
    color: ${(p) => getNewLookTokens(p.theme).muted};
    opacity: 0.7;
  }
  &:first-child::first-letter {
    text-transform: uppercase;
  }
`;

/**
 * The one muted meta line of a card ("Entries 1/1 · $200 committed · last
 * order 6h ago"). `pin` sinks it to the bottom of the section; otherwise it
 * hugs what is above it.
 */
export const NowMeta = ({
  items,
  pin = false,
  gap = 18,
}: {
  items: (ReactNode | undefined | false | "")[];
  pin?: boolean;
  gap?: number;
}) => {
  const shown = items.filter(Boolean);
  if (!shown.length) return null;
  return createElement(
    MetaLine,
    { $pin: pin, $gap: gap },
    createElement(
      MetaItems,
      null,
      shown.map((item, index) => createElement(MetaItem, { key: index }, item))
    )
  );
};
